/**
 * Тип дна по картам ГГК-1000: чистая часть оверлея — классы, цвета, подпись
 * полигона под точкой. Укладка на карту — `substrateLayer.ts`, запросы —
 * `useSubstrateLayer.ts`.
 */
import { SUBSTRATE_CLASS_COLORS } from '../brand/mapColors'
import { OVERLAY_PREFIX } from './layerOrder'

/** Классы модели в порядке легенды. */
export const SUBSTRATE_CLASSES = [1, 2, 3, 4, 5] as const

export const SUBSTRATE_SOURCE_ID = OVERLAY_PREFIX + 'substrate-src'
export const SUBSTRATE_FILL_LAYER = OVERLAY_PREFIX + 'substrate-fill'
export const SUBSTRATE_EDGE_LAYER = OVERLAY_PREFIX + 'substrate-edge'

/** Заливка полупрозрачная: под ней читается батиметрия, ради которой слой и
 *  смотрят рядом с глубиной. */
export const SUBSTRATE_FILL_OPACITY = 0.55

export function substrateColor(cls: number): string {
  return SUBSTRATE_CLASS_COLORS[cls] ?? SUBSTRATE_CLASS_COLORS[5]
}

/** Выражение MapLibre «класс → цвет». */
export function substrateColorExpression(): unknown[] {
  const expr: unknown[] = ['match', ['get', 'class5']]
  for (const c of SUBSTRATE_CLASSES) expr.push(c, substrateColor(c))
  expr.push(substrateColor(5))
  return expr
}

export interface SubstrateProps {
  sheet: string
  code: string
  label: string
  layer: 'lithology' | 'outcrop'
  class5: number
}

/** Классы, встретившиеся в кадре, по порядку легенды. */
export function presentClasses(values: number[]): number[] {
  const seen = new Set(values)
  return SUBSTRATE_CLASSES.filter(c => seen.has(c))
}

/** Ключ i18n названия класса. */
export function substrateClassKey(cls: number): string {
  return `substrate.class.${cls}`
}

/** Строка карточки отсчёта: «Песчаный алеврит (ПА) — ил · лист N-54».
 *  `className` — уже переведённое название класса. */
export function substrateLine(p: SubstrateProps, className: string, sheetWord: string): string {
  return `${p.label} (${p.code}) — ${className} · ${sheetWord} ${p.sheet}`
}

/** Свойства полигона из фичи MapLibre: свойства там приезжают уже без типов. */
export function substratePropsOf(raw: Record<string, unknown> | null | undefined): SubstrateProps | null {
  if (!raw) return null
  const cls = Number(raw.class5)
  if (!raw.code || !Number.isFinite(cls)) return null
  return {
    sheet: String(raw.sheet ?? ''),
    code: String(raw.code),
    label: String(raw.label ?? ''),
    layer: raw.layer === 'outcrop' ? 'outcrop' : 'lithology',
    class5: cls,
  }
}
